import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/i18n";
import { useAuth } from "@/auth";
import { useSEO } from "@/hooks/useSEO";

function signInWithGoogle() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSEO(t("login_title") + " — Flegrea Experience", t("tagline"));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.is_admin ? "/admin" : "/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 lg:py-36" data-testid="login-page">
      <div className="max-w-md mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="border border-stone2/60 bg-ivory p-8 md:p-10"
        >
          <h1 className="font-serif text-3xl md:text-4xl text-ink tracking-tight" data-testid="login-title">{t("login_title")}</h1>
          <p className="mt-2 text-sm text-ink/50">{t("login_sub")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("email_label")}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("password_label")}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-6 py-4 hover:bg-ink disabled:opacity-40 transition-colors duration-300"
            >
              <LogIn size={14} strokeWidth={1.5} /> {loading ? "..." : t("login_button")}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <span className="flex-1 border-t border-stone2/60" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{t("login_or")}</span>
            <span className="flex-1 border-t border-stone2/60" />
          </div>

          <button
            type="button"
            onClick={signInWithGoogle}
            data-testid="login-google-button"
            className="w-full flex items-center justify-center gap-2 border border-ink text-ink text-xs font-medium uppercase tracking-[0.2em] px-6 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300"
          >
            {t("login_google")}
          </button>

          <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-xs text-ink/50 hover:text-medblue transition-colors duration-300">
            <ArrowLeft size={13} strokeWidth={1.5} /> Flegrea Experience
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
